/* ============================================
   HOME PAGE
   The Creative Orchestra - GEMINI Framework
   Main landing page with orbital system
   ============================================ */

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Sparkles, 
  ArrowRight,
  Zap,
  Target,
  Layers,
  Eye,
} from 'lucide-react';
import { HeroSection } from '@components/landing';
import { ScrollReveal } from '@components/common';
import { GEMINI_STAGES, SITE_INFO } from '@utils/constants';
import { works, worksArray } from '@data';
import { getWorkTheme } from '@utils/workThemes';

/**
 * Home Page Component
 * Main landing page featuring:
 * - Hero section with orbital system
 * - Framework introduction
 * - Works overview by stage
 * - Call to action
 */
const HomePage = () => {
  // Set page title
  useEffect(() => {
    document.title = `${SITE_INFO.title} | ${SITE_INFO.subtitle}`;
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection />

      {/* Framework Introduction */}
      <FrameworkIntro />

      {/* Works by Stage */}
      <WorksOverview />

      {/* The Novel Section */}
      <NovelSection />

      {/* Call to Action */}
      <CallToAction />
    </div>
  );
};

/**
 * Framework Introduction Section
 */
const FrameworkIntro = () => {
  const stageIcons = {
    G: Zap,
    E: Target,
    M: Layers,
    IN: Eye,
  };

  return (
    <section className="relative py-24 md:py-32 px-4 bg-[#0a0a0f]">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6">
              The GEMINI Framework
            </h2>
            
            {/* GEMINI Abbreviation */}
            <div className="mb-6">
              <p className="text-lg text-gray-300">
                <span className="font-bold text-white">G</span>enerative{' '}
                <span className="font-bold text-white">E</span>cosystem for{' '}
                <span className="font-bold text-white">M</span>ulti-modal{' '}
                <span className="font-bold text-white">I</span>ntelligent{' '}
                <span className="font-bold text-white">N</span>avigation and{' '}
                <span className="font-bold text-white">I</span>deation
              </p>
            </div>
            
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              A holistic ecosystem transforming the design process from passive computer-aided 
              workflows to dynamic, agentic human-AI partnerships. The designer becomes 
              the conductor of an intelligent orchestra.
            </p>
          </div>
        </ScrollReveal>

        {/* Stage cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.values(GEMINI_STAGES).map((stage, index) => {
            const Icon = stageIcons[stage.letter] || Sparkles;
            
            return (
              <ScrollReveal key={stage.letter} delay={index * 0.1}>
                <motion.div
                  className="p-6 rounded-2xl bg-white/5 border border-white/10
                    hover:bg-white/10 hover:border-white/20 transition-all h-full"
                  whileHover={{ y: -4 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20
                      flex items-center justify-center">
                      <span className="text-xl font-bold text-white">{stage.letter}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{stage.name}</h3>
                      <p className="text-xs text-gray-500">{stage.works.length} system{stage.works.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    {getStageDescription(stage.letter)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {stage.works.map(workId => (
                      <Link
                        key={workId}
                        to={`/work/${workId}`}
                        className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400
                          hover:bg-white/10 hover:text-white transition-colors"
                      >
                        {works[workId]?.name || workId}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/**
 * Get stage description
 */
function getStageDescription(letter) {
  const descriptions = {
    G: 'AI-powered idea generation through intelligent provocation and collaborative brainstorming.',
    E: 'Quantitative assessment of ideas using vector embeddings and similarity analysis.',
    M: 'Version control and organization for the creative process with traceable evolution.',
    IN: 'Form generation inspired by moodboards, eye-tracking, and visual preferences.',
  };
  return descriptions[letter] || '';
}

/**
 * Works Overview Section
 */
const WorksOverview = () => {
  return (
    <section className="relative py-24 px-4">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0f1419] to-[#0a0a0f]" />
      
      <div className="relative z-10 max-w-6xl mx-auto">
        <ScrollReveal>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              Nine Interconnected Systems
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Each system addresses a specific aspect of the design workflow while 
              maintaining integration within the broader GEMINI ecosystem.
            </p>
          </div>
        </ScrollReveal>

        {/* Works grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {worksArray.map((work, index) => (
            <WorkCard key={work.id} work={work} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Work Card Component
 */
const WorkCard = ({ work, index }) => {
  const theme = getWorkTheme(work.id);
  const accentColor = theme?.colors?.accent || '#818CF8';

  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '129, 140, 248';
  };
  const accentRgb = hexToRgb(accentColor);

  return (
    <ScrollReveal delay={index * 0.05}>
      <Link to={`/work/${work.id}`}>
        <motion.div
          className="group p-6 rounded-2xl border transition-all duration-300 h-full"
          style={{
            background: `rgba(${accentRgb}, 0.05)`,
            borderColor: `rgba(${accentRgb}, 0.1)`,
          }}
          whileHover={{ 
            y: -6,
            borderColor: `rgba(${accentRgb}, 0.4)`,
            boxShadow: `0 20px 40px rgba(${accentRgb}, 0.15)`,
          }}
        >
          {/* Orb */}
          <motion.div 
            className="w-16 h-16 rounded-full mb-4 flex items-center justify-center
              border transition-all duration-300"
            style={{
              background: `linear-gradient(135deg, rgba(${accentRgb}, 0.3) 0%, rgba(${accentRgb}, 0.1) 100%)`,
              borderColor: `rgba(${accentRgb}, 0.3)`,
              boxShadow: `0 0 30px rgba(${accentRgb}, 0.2)`,
            }}
            whileHover={{ scale: 1.1 }}
          >
            <Sparkles className="w-6 h-6" style={{ color: accentColor }} />
          </motion.div>

          {/* Stage badge */}
          <span 
            className="text-xs font-medium px-2 py-0.5 rounded inline-block mb-2"
            style={{
              background: `rgba(${accentRgb}, 0.2)`,
              color: accentColor,
            }}
          >
            {work.stage}
          </span>

          {/* Name */}
          <h3 className="text-xl font-bold text-white mb-1 group-hover:text-indigo-300 transition-colors">
            {work.name}
          </h3>

          {/* Full name */}
          <p className="text-sm text-gray-400 mb-3">
            {work.fullName}
          </p>

          {/* Tagline */}
          <p className="text-sm text-gray-500 line-clamp-2">
            {work.tagline}
          </p>

          {/* Arrow */}
          <div className="mt-4 flex items-center gap-1 text-gray-500 
            group-hover:text-white transition-colors">
            <span className="text-sm">Explore</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </motion.div>
      </Link>
    </ScrollReveal>
  );
};

/**
 * Novel Section
 */
const NovelSection = () => {
  return (
    <section className="relative py-24 px-4 bg-[#0a0a0f]">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal>
          <div className="relative p-8 md:p-12 rounded-3xl overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 
              border border-white/10 rounded-3xl" />
            
            {/* Decorative orb */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full 
              bg-gradient-to-br from-indigo-500/20 to-transparent blur-3xl" />

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              {/* Icon */}
              <motion.div
                className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/20
                  flex items-center justify-center border border-white/20"
                animate={{
                  boxShadow: [
                    '0 0 30px rgba(129, 140, 248, 0.3)',
                    '0 0 50px rgba(129, 140, 248, 0.5)',
                    '0 0 30px rgba(129, 140, 248, 0.3)',
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <BookOpen className="w-10 h-10 text-white" />
              </motion.div>

              {/* Content */}
              <div className="flex-grow text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-3">
                  The Creative Orchestra
                </h2>
                <p className="text-gray-400 mb-6">
                  A Designer's Quest to Humanize Artificial Intelligence. 
                  Read the story behind GEMINI — 32 chapters exploring the journey from 
                  problem to solution.
                </p>
                <Link to="/novel">
                  <motion.button
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full
                      bg-gradient-to-r from-indigo-500 to-purple-500
                      text-white font-medium"
                    whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(129, 140, 248, 0.5)' }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <BookOpen className="w-5 h-5" />
                    Start Reading
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </Link>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

/**
 * Call to Action Section
 */
const CallToAction = () => {
  return (
    <section className="relative py-24 px-4 bg-[#0a0a0f]">
      <div className="max-w-4xl mx-auto text-center">
        <ScrollReveal>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
            From CAD to DAC
          </h2>
          <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            The transformation from Computer-Aided Design to Designers with Agentic Computers 
            represents a fundamental shift in how we approach creative work with AI.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/novel">
              <motion.button
                className="flex items-center gap-2 px-6 py-3 rounded-full
                  bg-white text-black font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <BookOpen className="w-5 h-5" />
                Read the Novel
              </motion.button>
            </Link>
            <Link to="/about">
              <motion.button
                className="flex items-center gap-2 px-6 py-3 rounded-full
                  bg-white/10 border border-white/20 text-white font-medium"
                whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.15)' }}
                whileTap={{ scale: 0.95 }}
              >
                <Sparkles className="w-5 h-5" />
                About the Research
              </motion.button>
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default HomePage;
